import { CSSProperties, useMemo } from 'react';
import { ModuleColorTheme } from '~/lib';
import { useFrame } from '~/shared/utils';
import { defaultColorTheme } from '~/shared/utils/defaultColorTheme';

/** The four colours a countdown paints with. A full CMS theme satisfies it. */
export type CountdownColorTheme = Pick<
    ModuleColorTheme,
    'mainColor' | 'foregroundColor' | 'highlightColor' | 'secondaryColor'
>;

const resolveCountdownTheme = (
    colorTheme: CountdownColorTheme | null | undefined,
    cmsTheme?: ModuleColorTheme | null,
): CountdownColorTheme => {
    // Passing `null` deliberately asks for the default; omitting it defers to the CMS.
    if (colorTheme !== undefined) return colorTheme ?? defaultColorTheme;

    return cmsTheme ?? defaultColorTheme;
};

const getThemeStyle = (theme: CountdownColorTheme): CSSProperties =>
    ({
        // SFCC/CMS theme values sometimes come in as "##RRGGBB" (double '#').
        // Collapse leading hashes so CSS variables remain valid colors.
        '--countdown-main': theme.mainColor?.replace(/^#+/, '#'),
        '--countdown-foreground': theme.foregroundColor?.replace(/^#+/, '#'),
        '--countdown-highlight': theme.highlightColor?.replace(/^#+/, '#'),
        '--countdown-secondary': theme.secondaryColor?.replace(/^#+/, '#'),
    }) as CSSProperties;

/**
 * The countdown's colours as CSS custom properties, ready to spread onto a `style` attribute.
 *
 * Resolution order is the override, then the CMS's `settings.countdownColorTheme`, then the
 * default theme - so a caller that wants the site's countdown colours passes nothing at all.
 */
export const useCountdownTheme = (colorTheme?: CountdownColorTheme | null): CSSProperties => {
    const { data: frame } = useFrame();

    return useMemo(
        () => getThemeStyle(resolveCountdownTheme(colorTheme, frame.settings?.countdownColorTheme)),
        [colorTheme, frame.settings?.countdownColorTheme],
    );
};
